import React from "react";
import { number } from "@euroland/format-number";

import i18n from "../../locale/i18n";
import PriceUpDown from "../ui/PriceUpDown";
import { TableData } from "@euroland/react-table";
import {
  POSITIVE_CHANGE_NUMBER_FORMAT,
  PRICE_STATUS,
  SETTING_FORMAT,
  TOOL_TEMPLATE,
} from "../../constant/common";
import {
  useCompanySettings,
  useSelectedInstrument,
  useTemplateSetting,
} from "../../hooks/useCompanySettings";
import { useAmountInvested } from "../../hooks/useTrades";
import { useInstrument } from "../../hooks/useInstruments";
import { formatDateTime, replaceKey, getDateFromDateTimeString } from "../../utils";
import { getCurrencyLabel } from "../../store/utils";
import { formatDate } from "@euroland/libs";
import useCurrencyName from "../../hooks/useCurrencyName";

const createTableData = ({
  data,
  currencyCode,
  template,
  getCurrencyNameByCode,
  companySettings,
  formatNumber,
}) => {
  let header, body;
  switch (template) {
    case TOOL_TEMPLATE.victor:
      header = {
        volume: {
          children: `${i18n.translate("value")} (${currencyCode})`,
          "aria-label": `${i18n.translate("value")} ${getCurrencyNameByCode(currencyCode)}`,
        },
        sharePrice: {
          children: `${i18n.translate("perShare")} (${currencyCode})`,
          "aria-label": `${i18n.translate("perShare")} ${getCurrencyNameByCode(currencyCode)}`,
        },
        change: {
          children: `${i18n.translate("change")} (${currencyCode})`,
          "aria-label": `${i18n.translate("change")} ${getCurrencyNameByCode(currencyCode)}`,
        },
        changePercentage: {
          children: `${i18n.translate("changePercent")}`,
        },
        annualized: {
          children: `${i18n.translate("oneYearChange")}`,
        },
      };
      body = data.map(item => {
        return {
          volume: {
            children: formatNumber(item.endValue),
            align: "right",
          },
          sharePrice: {
            children: formatNumber(item.endPrice),
            align: "right",
          },
          change: {
            children: (
              <PriceUpDown status={item.change >= 0 ? PRICE_STATUS.UP : PRICE_STATUS.DOWN}>
                <span>
                  {formatNumber(item.change, {
                    positiveFormatPattern:
                      companySettings.format?.positiveChangeFormat || POSITIVE_CHANGE_NUMBER_FORMAT,
                  })}
                </span>
              </PriceUpDown>
            ),
            align: "right",
          },
          changePercentage: {
            children: (
              <PriceUpDown status={item.changePro >= 0 ? PRICE_STATUS.UP : PRICE_STATUS.DOWN}>
                <span>
                  {formatNumber(item.changePro, {
                    positiveFormatPattern:
                      companySettings.format?.positiveChangeFormat || POSITIVE_CHANGE_NUMBER_FORMAT,
                  })}
                  %
                </span>
              </PriceUpDown>
            ),
            align: "right",
          },
          annualized: {
            children: (
              <PriceUpDown status={item.changePro >= 0 ? PRICE_STATUS.UP : PRICE_STATUS.DOWN}>
                <span>
                  {formatNumber(item.annualized, {
                    positiveFormatPattern:
                      companySettings.format?.positiveChangeFormat || POSITIVE_CHANGE_NUMBER_FORMAT,
                  })}
                  %
                </span>
              </PriceUpDown>
            ),
            align: "right",
          },
        };
      });
      break;
    case TOOL_TEMPLATE.quadrata:
      header = {
        volume: {
          children: `${i18n.translate("value")} (${currencyCode})`,
        },
        sharePrice: {
          children: `${i18n.translate("sharePrice")} (${currencyCode})`,
        },
        change: {
          children: `${i18n.translate("change")} (${currencyCode})`,
        },
        changePercentage: {
          children: `${i18n.translate("changePercentage")}`,
        },
        annualized: {
          children: `${i18n.translate("annualizedChange")}`,
        },
      };
      body = data.map(item => {
        return {
          volume: {
            children: formatNumber(item.endValue),
            align: "right",
          },
          sharePrice: {
            children: formatNumber(item.endPrice),
            align: "right",
          },
          change: {
            children: (
              <PriceUpDown status={item.change >= 0 ? PRICE_STATUS.UP : PRICE_STATUS.DOWN}>
                <span>{formatNumber(Math.abs(item.change))}</span>
              </PriceUpDown>
            ),
            align: "right",
          },
          changePercentage: {
            children: (
              <PriceUpDown status={item.changePro >= 0 ? PRICE_STATUS.UP : PRICE_STATUS.DOWN}>
                <span>{formatNumber(Math.abs(item.changePro))}</span>
              </PriceUpDown>
            ),
            align: "right",
          },
          annualized: {
            children: (
              <PriceUpDown status={item.annualized >= 0 ? PRICE_STATUS.UP : PRICE_STATUS.DOWN}>
                <span>{formatNumber(Math.abs(item.annualized))}</span>
              </PriceUpDown>
            ),
            align: "right",
          },
        };
      });
      break;
    default:
      break;
  }
  return { header, body };
};

function EndValueCard() {
  const [{ amountInvested, query }] = useAmountInvested();
  const [{ selectedInstrument }] = useInstrument();
  const companySettings = useCompanySettings();
  const template = useTemplateSetting();
  const getCurrencyNameByCode = useCurrencyName();

  const [, { formatNumber }] = useSelectedInstrument();

  const { header, body } = createTableData({
    data: [amountInvested],
    currencyCode: getCurrencyLabel(query.currency || selectedInstrument.currencyCode),
    template,
    companySettings,
    getCurrencyNameByCode,
    formatNumber,
  });
  let key = "";
  switch (template) {
    case TOOL_TEMPLATE.victor:
      key = "endValueWithBrackets";
      break;
    case TOOL_TEMPLATE.quadrata:
      key = "endValueWithHyphen";
      break;
    default:
      break;
  }
  const caption = replaceKey(i18n.translate(key), [
    formatDate(amountInvested.endDate, SETTING_FORMAT.FULL_DATE),
  ]);

  const newBody = Object.entries(header).map(([key, label]) => {
    const value = body[0][key];
    return {
      label, value
    }
  })
  
  const newHeader = {
    label: { children: 'Label'},
    value: { children: 'value'}
  }

  return (
    <>
      <div className="end-value-card">
        <h2 className="end-value-card__heading" aria-hidden="true">
          {replaceKey(i18n.translate(key), [formatDateTime(getDateFromDateTimeString(amountInvested.endDate), companySettings)])}
        </h2>
        <TableData
          className="end-value-card__table"
          design="table-quadrata"
          hiddenHeader={false}
          hiddenFooter={true}
          body={newBody}
          header={newHeader}
          headerLeft={false}
          caption={caption}
          containerProps={{}}
        ></TableData>
      </div>
    </>
  );
}
export default EndValueCard;
